<!--
  タブレットキオスクモード用デバイス管理
  (.steering/20260420-mobile-order-tablet-kiosk-mode/)

  admin権限で以下を管理:
  - 端末一覧（hall, table_number, mode, last_accessed_at, status）
  - 新規発行（hall + 任意のtable_number + 表示名）→ QRペアリング
  - pairing再発行（紛失・再セットアップ時）
  - 失効（discard）
-->
<template>
  <div class="mobile-order-device-list">
    <v-card flat>
      <v-card-text>
        <div class="d-flex justify-space-between align-center mb-4">
          <div>
            <div class="text-subtitle-1 font-weight-medium">
              タブレット端末管理
            </div>
            <div class="text-caption grey--text">
              卓や売場に設置するタブレットを登録し、固定モードで運用します
            </div>
          </div>
          <v-btn
            color="primary"
            :disabled="loading"
            @click="openCreateDialog"
          >
            <v-icon
              left
              small
            >
              {{ icons.mdiPlus }}
            </v-icon>
            新規発行
          </v-btn>
        </div>

        <v-data-table
          :headers="headers"
          :items="devices"
          :loading="loading"
          :no-data-text="loading ? '読み込み中...' : '登録された端末はありません'"
          :items-per-page="25"
          class="elevation-1"
        >
          <template #[`item.mode`]="{ item }">
            <v-chip
              :color="item.mode === 'fixed_table' ? 'primary' : 'secondary'"
              small
              outlined
            >
              {{ item.mode === 'fixed_table' ? '卓固定' : 'ホール固定' }}
            </v-chip>
          </template>

          <template #[`item.tableNumber`]="{ item }">
            {{ item.tableNumber != null ? resolveTableName(item.hall && item.hall.id, item.tableNumber) : '—' }}
          </template>

          <template #[`item.lastAccessedAt`]="{ item }">
            <span v-if="item.lastAccessedAt">{{ formatDateTime(item.lastAccessedAt) }}</span>
            <span
              v-else
              class="grey--text"
            >未接続</span>
          </template>

          <template #[`item.actions`]="{ item }">
            <v-btn
              icon
              small
              title="編集"
              @click="openEditDialog(item)"
            >
              <v-icon small>
                {{ icons.mdiPencil }}
              </v-icon>
            </v-btn>
            <v-btn
              icon
              small
              title="QRコード再発行"
              @click="openRegenerateDialog(item)"
            >
              <v-icon small>
                {{ icons.mdiQrcode }}
              </v-icon>
            </v-btn>
            <v-btn
              icon
              small
              title="失効"
              color="error"
              @click="openDiscardDialog(item)"
            >
              <v-icon small>
                {{ icons.mdiDelete }}
              </v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- 新規発行ダイアログ -->
    <v-dialog
      v-model="createDialog"
      max-width="500"
      persistent
    >
      <v-card>
        <v-card-title>新規端末を発行</v-card-title>
        <v-card-text>
          <v-form
            ref="createForm"
            v-model="createFormValid"
          >
            <v-select
              v-model="createForm.hallId"
              :items="hallOptions"
              item-text="name"
              item-value="id"
              label="ホール"
              :rules="[v => !!v || 'ホールを選択してください']"
              required
              @change="createForm.tableNumber = null"
            />
            <v-select
              v-model="createForm.tableNumber"
              :items="tableNumberOptions"
              item-text="label"
              item-value="value"
              label="卓番号（任意）"
              :disabled="!createForm.hallId"
              hint="「ホール固定モード」を選ぶとこの端末はホール内のどの卓でも操作可能になります"
              persistent-hint
            />
            <v-text-field
              v-model="createForm.name"
              label="表示名"
              :rules="[v => !!v || '表示名を入力してください']"
              placeholder="例: 1F 5卓タブレット"
              required
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="submitting"
            @click="closeCreateDialog"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="primary"
            :loading="submitting"
            :disabled="!createFormValid"
            @click="submitCreate"
          >
            発行
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 編集ダイアログ -->
    <v-dialog
      v-model="editDialog"
      max-width="500"
      persistent
    >
      <v-card>
        <v-card-title>端末を編集</v-card-title>
        <v-card-text>
          <v-form
            ref="editForm"
            v-model="editFormValid"
          >
            <v-select
              v-model="editForm.hallId"
              :items="hallOptions"
              item-text="name"
              item-value="id"
              label="ホール"
              :rules="[v => !!v || 'ホールを選択してください']"
              required
              @change="editForm.tableNumber = null"
            />
            <v-select
              v-model="editForm.tableNumber"
              :items="editTableNumberOptions"
              item-text="label"
              item-value="value"
              label="卓番号（任意）"
              :disabled="!editForm.hallId"
              hint="「ホール固定モード」を選ぶとこの端末はホール内のどの卓でも操作可能になります"
              persistent-hint
            />
            <v-text-field
              v-model="editForm.name"
              label="表示名"
              :rules="[v => !!v || '表示名を入力してください']"
              required
            />
            <v-alert
              dense
              type="info"
              outlined
              class="mt-3 mb-0 text-caption"
            >
              端末の再ペアリングは不要です。運用中の端末はそのまま新しい設定で動作します。
            </v-alert>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="updating"
            @click="closeEditDialog"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="primary"
            :loading="updating"
            :disabled="!editFormValid"
            @click="submitEdit"
          >
            保存
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- QRペアリングダイアログ -->
    <v-dialog
      v-model="pairingDialog"
      max-width="480"
      persistent
    >
      <v-card>
        <v-card-title>
          タブレットでQRをスキャンしてください
        </v-card-title>
        <v-card-text class="text-center">
          <div class="mb-3">
            <strong>{{ pairingDevice && pairingDevice.name }}</strong>
          </div>
          <div class="d-flex justify-center mb-4">
            <canvas ref="qrCanvas" />
          </div>
          <v-alert
            v-if="remainingSeconds > 0"
            dense
            type="info"
            outlined
          >
            有効期限: あと {{ remainingSeconds }} 秒
          </v-alert>
          <v-alert
            v-else
            dense
            type="warning"
            outlined
          >
            有効期限切れです。「再発行」を押してください
          </v-alert>
          <div class="text-caption grey--text mt-3">
            QRコードが読めない場合のコード:
            <code class="mono-code">{{ pairingToken }}</code>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            :disabled="remainingSeconds > 0"
            :loading="regenerating"
            @click="regenerate"
          >
            再発行
          </v-btn>
          <v-btn
            text
            @click="closePairingDialog"
          >
            閉じる
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 失効確認ダイアログ -->
    <v-dialog
      v-model="discardDialog"
      max-width="400"
      persistent
    >
      <v-card>
        <v-card-title>端末を失効しますか？</v-card-title>
        <v-card-text>
          <div>
            <strong>{{ discardTarget && discardTarget.name }}</strong> を失効します。
          </div>
          <div class="text-caption grey--text mt-2">
            この端末からのアクセスは即座に拒否されます。再利用する場合は新しい端末として再発行してください。
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="discarding"
            @click="closeDiscardDialog"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="error"
            :loading="discarding"
            @click="confirmDiscard"
          >
            失効
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
    >
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import {
  ref, reactive, computed, onMounted, onBeforeUnmount, nextTick,
} from '@vue/composition-api'
import {
  mdiPlus, mdiQrcode, mdiDelete, mdiPencil,
} from '@mdi/js'
import QrCode from 'qrcode'
import MobileOrderDeviceApi from '@/api/waiter/MobileOrderDevice'
import HallApi from '@/api/waiter/Hall'

export default {
  name: 'MobileOrderDeviceList',

  setup() {
    const loading = ref(false)
    const submitting = ref(false)
    const regenerating = ref(false)
    const discarding = ref(false)
    const updating = ref(false)

    const devices = ref([])
    const hallOptions = ref([])

    const createDialog = ref(false)
    const createFormValid = ref(false)
    const createForm = reactive({
      hallId: null,
      tableNumber: null,
      name: '',
    })
    const createFormRef = ref(null)

    const editDialog = ref(false)
    const editFormValid = ref(false)
    const editingId = ref(null)
    const editForm = reactive({
      hallId: null,
      tableNumber: null,
      name: '',
    })

    const pairingDialog = ref(false)
    const pairingDevice = ref(null)
    const pairingToken = ref('')
    const pairingExpiresAt = ref(null)
    const qrCanvas = ref(null)
    const remainingSeconds = ref(0)
    let countdownTimer = null

    const discardDialog = ref(false)
    const discardTarget = ref(null)

    const snackbar = reactive({ show: false, color: 'success', message: '' })

    const headers = [
      { text: '表示名', value: 'name' },
      { text: 'ホール', value: 'hall.name' },
      { text: '卓番号', value: 'tableNumber', align: 'center' },
      { text: 'モード', value: 'mode', align: 'center' },
      { text: '最終アクセス', value: 'lastAccessedAt' },
      {
        text: '操作', value: 'actions', sortable: false, align: 'end',
      },
    ]

    const showSnackbar = (message, color = 'success') => {
      snackbar.show = true
      snackbar.color = color
      snackbar.message = message
    }

    const formatDateTime = iso => {
      if (!iso) return ''
      const d = new Date(iso)

      return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    }

    // camelCase変換（axios interceptorが既にcamelizeしている前提だが、念のため）
    const toCamelDevice = d => ({
      id: d.id,
      name: d.name,
      mode: d.mode,
      hall: d.hall,
      tableNumber: d.tableNumber ?? d.table_number ?? null,
      lastAccessedAt: d.lastAccessedAt ?? d.last_accessed_at ?? null,
      active: d.active,
    })

    const fetchDevices = async () => {
      loading.value = true
      try {
        const res = await MobileOrderDeviceApi.index()
        devices.value = (res.data.devices || []).map(toCamelDevice)
      } catch (e) {
        console.error('Failed to fetch devices:', e)
        showSnackbar('端末一覧の取得に失敗しました', 'error')
      } finally {
        loading.value = false
      }
    }

    const fetchHalls = async () => {
      try {
        const res = await HallApi.getHalls({ isSettings: true })
        const data = res.data.halls?.data || []
        hallOptions.value = data.map(h => ({
          id: Number(h.id),
          name: h.attributes.name,
          tableCount: h.attributes.tableCount ?? h.attributes.table_count ?? 12,
          tablePrefix: h.attributes.tablePrefix ?? h.attributes.table_prefix ?? '',
          tableSuffix: h.attributes.tableSuffix ?? h.attributes.table_suffix ?? '',
          tableLayouts: h.attributes.tableLayouts ?? h.attributes.table_layouts ?? [],
        }))
      } catch (e) {
        console.error('Failed to fetch halls:', e)
      }
    }

    // フロアレイアウトのカスタム卓名（hall.tableLayouts[].name）を優先し、
    // 未設定なら prefix + 卓番号 + suffix にフォールバック（Table#name と同等）
    const resolveTableName = (hallId, tableNumber) => {
      const hall = hallOptions.value.find(h => h.id === Number(hallId))
      if (!hall) return `${tableNumber}卓`
      const layout = (hall.tableLayouts || []).find(l => Number(l.table_number) === Number(tableNumber))

      return layout?.name || `${hall.tablePrefix || ''}${tableNumber}${hall.tableSuffix || ''}`
    }

    // 選択中ホールの table_count に応じた卓番号オプション（1..N + "ホール固定"）
    const buildTableNumberOptions = hallId => {
      const selected = hallOptions.value.find(h => h.id === hallId)
      const count = selected?.tableCount ?? 0
      const numbers = Array.from({ length: count }, (_, i) => ({
        label: resolveTableName(hallId, i + 1),
        value: i + 1,
      }))

      return [
        { label: '（ホール固定モード）', value: null },
        ...numbers,
      ]
    }

    const tableNumberOptions = computed(() => buildTableNumberOptions(createForm.hallId))
    const editTableNumberOptions = computed(() => buildTableNumberOptions(editForm.hallId))

    const openCreateDialog = () => {
      createForm.hallId = null
      createForm.tableNumber = null
      createForm.name = ''
      createDialog.value = true
    }

    const closeCreateDialog = () => {
      createDialog.value = false
    }

    const clearCountdown = () => {
      if (countdownTimer) {
        clearInterval(countdownTimer)
        countdownTimer = null
      }
    }

    const startCountdown = () => {
      clearCountdown()
      const update = () => {
        if (!pairingExpiresAt.value) {
          remainingSeconds.value = 0

          return
        }
        const diff = Math.floor((new Date(pairingExpiresAt.value).getTime() - Date.now()) / 1000)
        remainingSeconds.value = Math.max(0, diff)
        if (remainingSeconds.value === 0) clearCountdown()
      }
      update()
      countdownTimer = setInterval(update, 1000)
    }

    const drawQr = async payload => {
      await nextTick()
      if (!qrCanvas.value) return
      QrCode.toCanvas(qrCanvas.value, payload, { width: 240, margin: 1 }, err => {
        if (err) console.error('QRコード生成エラー:', err)
      })
    }

    const openPairingFromResponse = ({ device, pairing }) => {
      pairingDevice.value = toCamelDevice(device)
      pairingToken.value = pairing.token
      pairingExpiresAt.value = pairing.expiresAt ?? pairing.expires_at
      pairingDialog.value = true
      startCountdown()
      drawQr(pairing.qrPayload ?? pairing.qr_payload)
    }

    const closePairingDialog = () => {
      pairingDialog.value = false
      pairingDevice.value = null
      pairingToken.value = ''
      pairingExpiresAt.value = null
      clearCountdown()
    }

    const submitCreate = async () => {
      submitting.value = true
      try {
        const res = await MobileOrderDeviceApi.create({
          hallId: createForm.hallId,
          tableNumber: createForm.tableNumber || null,
          name: createForm.name,
        })
        showSnackbar('端末を発行しました。QRをスキャンしてください')
        await fetchDevices()
        closeCreateDialog()
        openPairingFromResponse(res.data)
      } catch (e) {
        console.error('Failed to create device:', e)
        showSnackbar(e.response?.data?.message || '端末の発行に失敗しました', 'error')
      } finally {
        submitting.value = false
      }
    }

    const openEditDialog = device => {
      editingId.value = device.id
      editForm.hallId = device.hall?.id ?? null
      editForm.tableNumber = device.tableNumber ?? null
      editForm.name = device.name ?? ''
      editDialog.value = true
    }

    const closeEditDialog = () => {
      editDialog.value = false
      editingId.value = null
    }

    const submitEdit = async () => {
      if (!editingId.value) return
      updating.value = true
      try {
        await MobileOrderDeviceApi.update(editingId.value, {
          hallId: editForm.hallId,
          tableNumber: editForm.tableNumber, // null 許容（fixed_hall モード）
          name: editForm.name,
        })
        showSnackbar('端末を更新しました')
        await fetchDevices()
        closeEditDialog()
      } catch (e) {
        console.error('Failed to update device:', e)
        showSnackbar(e.response?.data?.message || '端末の更新に失敗しました', 'error')
      } finally {
        updating.value = false
      }
    }

    const openRegenerateDialog = async device => {
      pairingDevice.value = device
      try {
        regenerating.value = true
        const res = await MobileOrderDeviceApi.regeneratePairing(device.id)
        openPairingFromResponse(res.data)
      } catch (e) {
        console.error('Failed to regenerate pairing:', e)
        showSnackbar('QR再発行に失敗しました', 'error')
      } finally {
        regenerating.value = false
      }
    }

    const regenerate = async () => {
      if (!pairingDevice.value) return
      regenerating.value = true
      try {
        const res = await MobileOrderDeviceApi.regeneratePairing(pairingDevice.value.id)
        openPairingFromResponse(res.data)
      } catch (e) {
        console.error('Failed to regenerate pairing:', e)
        showSnackbar('QR再発行に失敗しました', 'error')
      } finally {
        regenerating.value = false
      }
    }

    const openDiscardDialog = device => {
      discardTarget.value = device
      discardDialog.value = true
    }

    const closeDiscardDialog = () => {
      discardDialog.value = false
      discardTarget.value = null
    }

    const confirmDiscard = async () => {
      if (!discardTarget.value) return
      discarding.value = true
      try {
        await MobileOrderDeviceApi.destroy(discardTarget.value.id)
        showSnackbar('端末を失効しました')
        await fetchDevices()
        closeDiscardDialog()
      } catch (e) {
        console.error('Failed to discard device:', e)
        showSnackbar('失効に失敗しました', 'error')
      } finally {
        discarding.value = false
      }
    }

    onMounted(() => {
      fetchDevices()
      fetchHalls()
    })

    onBeforeUnmount(() => clearCountdown())

    return {
      icons: {
        mdiPlus, mdiQrcode, mdiDelete, mdiPencil,
      },
      loading,
      submitting,
      regenerating,
      discarding,
      updating,
      devices,
      hallOptions,
      resolveTableName,
      tableNumberOptions,
      editTableNumberOptions,
      headers,
      createDialog,
      createForm,
      createFormValid,
      createFormRef,
      openCreateDialog,
      closeCreateDialog,
      submitCreate,
      editDialog,
      editForm,
      editFormValid,
      openEditDialog,
      closeEditDialog,
      submitEdit,
      pairingDialog,
      pairingDevice,
      pairingToken,
      remainingSeconds,
      qrCanvas,
      closePairingDialog,
      regenerate,
      openRegenerateDialog,
      discardDialog,
      discardTarget,
      openDiscardDialog,
      closeDiscardDialog,
      confirmDiscard,
      snackbar,
      formatDateTime,
    }
  },
}
</script>

<style scoped>
.mono-code {
  user-select: all;
  font-family: ui-monospace, SFMono-Regular, monospace;
  font-size: 12px;
  word-break: break-all;
}
</style>
